/**
 * Brand color helpers — framework-free, importable by both the Vite config
 * (Node, build-time inline `<style>`) and the browser (`BrandingProvider`).
 *
 * The whole portal themes off ONE source: the brand ramp. These helpers turn a
 * brand's `colorTokens` into the CSS custom properties that drive it:
 *   --mz-brand-<step>       (hex)          → legacy `.brand-*` utils, mazeed primitives
 *   --mz-brand-<step>-hsl   ("H S% L%")    → Tailwind `brand-*` scale + semantic tokens
 *   --mz-brand-hue          (number)       → dark-mode tints that track the brand hue
 *
 * Keeping a single mapping here means the build-time inline style and the
 * runtime provider can never drift apart.
 */
import type { TenantBranding } from '@/features/platform/api';

const RAMP_STEPS = [50, 100, 200, 300, 400, 500, 600, 700] as const;
type RampStep = (typeof RAMP_STEPS)[number];

/* ─── hex ⇄ hsl ──────────────────────────────────────────────────────── */

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function normalizeHex(hex: string): string {
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return h;
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = normalizeHex(hex);
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(r: number, g: number, b: number): string {
  const to = (v: number) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** Returns HSL with h∈[0,360), s,l∈[0,100]. */
export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const [r255, g255, b255] = hexToRgb(hex);
  const r = r255 / 255, g = g255 / 255, b = b255 / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const d = max - min;
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: h = ((g - b) / d) % 6; break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: s * 100, l: l * 100 };
}

/** Tailwind/shadcn channel string, e.g. "24 94% 53%" (for `hsl(var(--x))`). */
export function hexToHslChannels(hex: string): string {
  const { h, s, l } = hexToHsl(hex);
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;
}

/** Blend two hex colors: amount 0 → a, 1 → b. */
function mix(a: string, b: string, amount: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const t = clamp(amount, 0, 1);
  return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

/* ─── ramp resolution ────────────────────────────────────────────────── */

/**
 * Derive a full 50→700 ramp from a single primary hex — tints blend toward
 * white, shades toward black. Mirrors the mobile app's `useBrandRamp`, so a
 * tenant that supplies only `primary` still themes the entire UI.
 */
export function deriveRamp(primary: string): Record<RampStep, string> {
  const WHITE = '#FFFFFF', BLACK = '#000000';
  return {
    50: mix(primary, WHITE, 0.92),
    100: mix(primary, WHITE, 0.82),
    200: mix(primary, WHITE, 0.6),
    300: mix(primary, WHITE, 0.38),
    400: mix(primary, WHITE, 0.16),
    500: primary,
    600: mix(primary, BLACK, 0.12),
    700: mix(primary, BLACK, 0.24),
  };
}

/** Resolve the effective ramp from branding tokens (explicit steps win). */
function resolveRamp(branding: TenantBranding): Record<RampStep, string> | null {
  const tokens = branding.colorTokens;
  if (!tokens) return null;
  const explicit: Partial<Record<RampStep, string>> = {};
  for (const step of RAMP_STEPS) {
    const v = tokens[`brand${step}`];
    if (v) explicit[step] = v;
  }
  const primary = tokens.brand500 ?? tokens.primary ?? explicit[500];
  // Nothing usable → let the CSS defaults stand.
  if (!primary && Object.keys(explicit).length === 0) return null;
  const base = primary ? deriveRamp(primary) : ({} as Record<RampStep, string>);
  return { ...base, ...explicit } as Record<RampStep, string>;
}

/**
 * Full set of brand CSS variables for a branding config. Empty object when the
 * branding carries no usable color — callers then leave the CSS defaults.
 */
export function brandCssVars(branding: TenantBranding): Record<string, string> {
  const ramp = resolveRamp(branding);
  if (!ramp) return {};
  const vars: Record<string, string> = {};
  for (const step of RAMP_STEPS) {
    const hex = ramp[step];
    vars[`--mz-brand-${step}`] = hex;
    vars[`--mz-brand-${step}-hsl`] = hexToHslChannels(hex);
  }
  vars['--mz-brand-hue'] = String(Math.round(hexToHsl(ramp[500]).h));
  return vars;
}

/** Serialize brand vars into a `:root{…}` rule for a build-time inline style. */
export function brandCssRootRule(branding: TenantBranding): string {
  const vars = brandCssVars(branding);
  const body = Object.entries(vars).map(([k, v]) => `${k}:${v}`).join(';');
  return body ? `:root{${body}}` : '';
}
